import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

// Types
export interface ExtractedDate {
  date: string;
  context: string;
  confidence?: number;
}

export interface ExtractedDates {
  dates: ExtractedDate[];
}

export interface ReferencedDocument {
  document_id: string;
  document_name: string;
  document_type: string;
  file_url: string;
  property_id?: string;
  property_name?: string;
  page_number?: number;
  similarity_score?: number;
  dates?: ExtractedDates;
}

export interface ChatFileAttachment {
  id: string;
  name: string;
  type: string;
  url?: string;
}

export interface ChatMessage {
  message_id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  referenced_documents?: ReferencedDocument[];
  confidence?: number;
  files?: ChatFileAttachment[];
}

export interface SendMessageRequest {
  query: string;
  property_id?: string;
  prompt_mode?: "property" | "general";
  files?: File[];
}

export interface SendMessageResponse {
  answer: string;
  referenced_documents: ReferencedDocument[];
  confidence?: number;
}

export interface GetMessagesRequest {
  limit?: number;
  next_token?: string;
}

export interface GetMessagesResponse {
  messages: ChatMessage[];
  next_token?: string;
}

export interface DeleteMessagesRequest {
  // No parameters needed - uses token
}

export interface DeleteMessagesResponse {
  success: boolean;
  deleted_count: number;
}

interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  nextToken?: string;
  hasMore: boolean;
}

const initialState: ChatState = {
  messages: [],
  isLoading: false,
  isSending: false,
  error: null,
  nextToken: undefined,
  hasMore: true,
};

// Async Thunks

// チャット送信
export const sendChatMessage = createAsyncThunk<
  { message: ChatMessage; response: SendMessageResponse },
  SendMessageRequest
>("chat/sendMessage", async (request, { rejectWithValue }) => {
  try {
    let payload: any;
    let config: any = {};

    // ファイルがある場合はFormDataで送信
    if (request.files && request.files.length > 0) {
      const formData = new FormData();
      formData.append("query", request.query);
      if (request.property_id) {
        formData.append("property_id", request.property_id);
      }
      if (request.prompt_mode) {
        formData.append("prompt_mode", request.prompt_mode);
      }
      // ファイルを追加
      request.files.forEach((file) => {
        formData.append("files", file);
      });
      payload = formData;
      config.headers = {
        "Content-Type": "multipart/form-data",
      };
    } else {
      // ファイルがない場合はJSONで送信
      payload = {
        query: request.query,
        property_id: request.property_id,
        prompt_mode: request.prompt_mode,
      };
    }

    const response = await ipponApi.post(
      `${BaseURL}/api/v1/chat/messages`,
      payload,
      config
    );

    // アシスタントメッセージを作成
    const assistantMessage: ChatMessage = {
      message_id: `assistant-${Date.now()}`,
      role: "assistant",
      content: response.data.answer,
      timestamp: new Date().toISOString(),
      referenced_documents: response.data.referenced_documents,
      confidence: response.data.confidence,
    };

    return {
      message: assistantMessage,
      response: response.data,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to send message"
    );
  }
});

// メッセージ履歴取得
export const getChatMessages = createAsyncThunk<
  GetMessagesResponse,
  GetMessagesRequest
>("chat/getMessages", async (request, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({
      limit: (request.limit || 20).toString(),
    });

    if (request.next_token) {
      params.append("next_token", request.next_token);
    }

    const response = await ipponApi.get(
      `${BaseURL}/api/v1/chat/messages?${params.toString()}`
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to fetch messages"
    );
  }
});

// メッセージ履歴削除
export const deleteChatMessages = createAsyncThunk<
  DeleteMessagesResponse,
  DeleteMessagesRequest | void
>("chat/deleteMessages", async (_request, { rejectWithValue }) => {
  try {
    const response = await ipponApi.delete(`${BaseURL}/api/v1/chat/messages`);

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to delete messages"
    );
  }
});

// Slice
const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    // メッセージをクリア
    clearMessages: (state) => {
      state.messages = [];
      state.nextToken = undefined;
      state.hasMore = true;
      state.error = null;
    },
    // エラーをクリア
    clearError: (state) => {
      state.error = null;
    },
    // ユーザーメッセージを即座に追加（楽観的UI更新）
    addUserMessage: (
      state,
      action: PayloadAction<{ content: string; files?: ChatFileAttachment[] }>
    ) => {
      const userMessage: ChatMessage = {
        message_id: `temp-user-${Date.now()}`,
        role: "user",
        content: action.payload.content,
        timestamp: new Date().toISOString(),
        files:
          action.payload.files && action.payload.files.length > 0
            ? action.payload.files
            : undefined,
      };
      state.messages.push(userMessage);
    },
  },
  extraReducers: (builder) => {
    // sendChatMessage
    builder
      .addCase(sendChatMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.isSending = false;
        // アシスタントメッセージを追加
        state.messages.push(action.payload.message);
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload as string;
      });

    // getChatMessages
    builder
      .addCase(getChatMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getChatMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        // メッセージを追加（古いメッセージは先頭に追加）
        // APIから降順で返ってくるので、逆順にしてから追加
        const sortedMessages = [...action.payload.messages].reverse();
        state.messages = [...sortedMessages, ...state.messages];
        state.nextToken = action.payload.next_token;
        state.hasMore = !!action.payload.next_token;
      })
      .addCase(getChatMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // deleteChatMessages
    builder
      .addCase(deleteChatMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteChatMessages.fulfilled, (state) => {
        state.isLoading = false;
        state.messages = [];
        state.nextToken = undefined;
        state.hasMore = true;
      })
      .addCase(deleteChatMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearMessages, clearError, addUserMessage } = chatSlice.actions;
export default chatSlice.reducer;
